/**
 * Citation categorization utilities
 */

import type { Document } from "@/api/models"

import { resolveLibraryCitationSource } from "@/utils/citation-utils"

import { normalizeTextFragmentUrl } from "./url-utils"

export type CategorizedCitation = {
  citation: string
  index: number
}

export type CategorizedCitations = {
  webSources: CategorizedCitation[]
  libraryDocuments: CategorizedCitation[]
  userDocuments: CategorizedCitation[]
  uploadedFiles: CategorizedCitation[]
  m365Sources: CategorizedCitation[]
}

/**
 * Check if a citation is a URL
 */
export const isUrl = (citation: string): boolean => {
  return citation.startsWith("http://") || citation.startsWith("https://")
}

/**
 * Categorize citations into web, library, user documents, uploaded files, and M365 sources
 */
export const categorizeCitations = (
  citations: string[],
  documents: [string, Document][] = []
): CategorizedCitations => {
  return citations.reduce(
    (acc, citation, index) => {
      const normalizedCitation = normalizeTextFragmentUrl(citation)
      const citationSource = resolveLibraryCitationSource(normalizedCitation, documents)

      if (isUrl(citationSource)) {
        acc.webSources.push({ citation: normalizedCitation, index })
      } else if (citationSource.startsWith("req:")) {
        // Extraction references are rendered by tender-specific UI, not the shared accordion
      } else {
        if (citationSource.startsWith("doc:")) {
          acc.userDocuments.push({ citation: normalizedCitation, index })
        } else if (citationSource.includes(":")) {
          const libraryId = citationSource.split(":")[0]
          if (libraryId === "localdocs") {
            acc.uploadedFiles.push({ citation: normalizedCitation, index })
          } else if (libraryId === "m365") {
            acc.m365Sources.push({ citation: normalizedCitation, index })
          } else {
            acc.libraryDocuments.push({ citation: normalizedCitation, index })
          }
        } else {
          acc.uploadedFiles.push({ citation: normalizedCitation, index })
        }
      }
      return acc
    },
    {
      webSources: [] as CategorizedCitation[],
      libraryDocuments: [] as CategorizedCitation[],
      userDocuments: [] as CategorizedCitation[],
      uploadedFiles: [] as CategorizedCitation[],
      m365Sources: [] as CategorizedCitation[],
    }
  )
}
